<script setup lang="ts">
import { computed, ref, useAttrs, useSlots } from "vue";
import { vOnClickOutside } from "@vueuse/components";
import ClearIcon from "@magnit/icons/src/assets/icons/close-24-system.svg";
import ClearRedIcon from "@magnit/icons/src/assets/icons/circle-error-24-system.svg";
import type { IInputEmits, IInputProps } from "./VInput.types";

const props = withDefaults(defineProps<IInputProps>(), {
  wrapperClass: undefined,
  decoratorClass: undefined,
  label: undefined,
  hint: undefined,
  error: undefined,
  prefix: undefined,
  suffix: undefined,
  clearable: true,
  forceClearable: false,
  placeholder: undefined, // возможно явные дефолты undefined тут излишне, но добавил тоже чисто для общего стиля
  clickOutsideIgnoreRefs: () => [],
  keepFocusAfterClear: true,
});
const emit = defineEmits<IInputEmits>();
defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const slots = useSlots();

const inputRef = ref<HTMLInputElement | null>(null);
const isFocused = ref(false);

const hasModelValue = computed(() => Boolean(`${props.modelValue}`));

const rootClass = computed(() => [
  props.wrapperClass,
  {
    "pl-input_error": props.hasError,
    "pl-input_success": props.success,
    "pl-input_has-focus": isFocused.value,
    "pl-input_has-value": hasModelValue.value,
    "pl-input_has-label": props.label,
    "pl-input_has-clear": props.clearable,
    "pl-input_has-disabled": props.disabled,
    "pl-input_has-required": props.required,
    "pl-input_has-readonly": props.readonly,
  },
]);

const focus = () => {
  if (props.disabled) {
    return;
  }

  // @NOTE: hack for iOS cause w/o setTimeout focus
  // instantly triggers field but phone can't position keyboard correctly
  setTimeout(() => {
    inputRef.value?.focus();
    isFocused.value = true;
    emit("focus");
  }, 150);
};

const placeholderText = computed(() => {
  if (!props.label) {
    return props.placeholder;
  }
  // Обсуждали с дизайнерами, в случае когда у нас и label и placeholder
  // placeholder показываем только когда инпут в фокусе.
  // В целом логика такая, что:
  // label это пояснение про назначение поля (например "Телефон"),
  // placeholder это пояснение про формат данных в поле (например "+7-123-45-67").
  // Поэтому логично что label первичнее, а placeholder показывается только после взаимодействия с полем или в случае отсутвия label.
  return isFocused.value ? props.placeholder : "";
});

const showClearIcon = computed(
  () =>
    props.forceClearable ||
    !!(
      props.clearable &&
      hasModelValue.value &&
      !props.disabled &&
      !props.readonly
    ),
);

const clear = () => {
  emit("update:modelValue", "");
  emit("clear");

  if (props.keepFocusAfterClear) {
    inputRef.value?.focus();
  }
};

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const onClickControl = () => {
  if (isFocused.value || props.disabled) {
    return;
  }

  inputRef.value?.focus();

  isFocused.value = true;
  emit("focus");
};

const onClickOutsideControl = () => {
  if (!isFocused.value || props.disabled) {
    return;
  }

  inputRef.value?.blur();

  isFocused.value = false;
  emit("blur");
};

defineExpose({
  blur: onClickOutsideControl,
  focus,
  inputRef,
});
</script>

<template>
  <div
    class="pl-input-wrapper"
    :class="[
      ...rootClass,
      !!slots['iconLeft'] && 'pl-input_has-left-icon',
      !!slots['iconRight'] && 'pl-input_has-right-icon',
    ]"
  >
    <div
      v-on-click-outside="[
        onClickOutsideControl,
        { ignore: clickOutsideIgnoreRefs },
      ]"
      class="pl-input"
      @click="onClickControl"
    >
      <input
        v-bind="attrs"
        ref="inputRef"
        :value="modelValue"
        :disabled="disabled"
        :readonly="readonly"
        :placeholder="placeholderText"
        class="pl-input-field"
        @input="onInput"
        @focus="onClickControl"
      />

      <span v-if="props.label" class="pl-input-label">{{ label }}</span>

      <span :class="(prefix || !!slots['prefix']) && 'pl-input-prefix'">
        {{ prefix }}
        <slot name="prefix" />
      </span>

      <span v-if="suffix" class="pl-input-suffix">
        {{ suffix }}
      </span>

      <span :class="!!slots['iconLeft'] && 'pl-input-icon-left'">
        <slot name="iconLeft" />
      </span>

      <span v-if="showClearIcon" class="pl-input-clear" @click.stop="clear">
        <ClearRedIcon v-if="hasError" />
        <ClearIcon v-else />
      </span>

      <span :class="!!slots['iconRight'] && 'pl-input-icon-right'">
        <slot name="iconRight" />
      </span>
    </div>

    <span v-if="hint" class="pl-input-hint">
      {{ hint }}
    </span>

    <span v-if="error" class="pl-input-hint-error">
      {{ error }}
    </span>
  </div>
</template>

<style lang="postcss" scoped>
.pl-input {
  position: relative;
  height: 48px;
  display: flex;
  align-items: center;
  background-color: var(--pl-field-background-default);
  padding: 0 var(--pl-unit-x3);
  border-radius: var(--pl-field-radius-l);

  &:hover {
    background-color: var(--pl-field-background-hover);
  }

  .pl-input_error & {
    background-color: var(--pl-system-error-weak-default);
  }

  .pl-input_success & {
    background-color: var(--pl-system-success-weak-default);
  }

  .pl-input_has-focus & {
    background-color: var(--pl-field-background-pressed);
  }

  .pl-input_has-disabled & {
    background-color: var(--pl-field-background-disabled);
  }

  .pl-input_has-disabled &::before {
    color: var(--pl-text-tertiary);
  }

  .pl-input_error.pl-input_has-focus & {
    background-color: var(--pl-system-error-weak-default);
  }

  .pl-input_error.pl-input_has-disabled & {
    background-color: var(--pl-system-error-weak-default);
  }

  .pl-input_success.pl-input_has-focus & {
    background-color: var(--pl-system-success-weak-default);
  }
}

.pl-input-field {
  position: relative;
  font: var(--pl-font-body-large-regular);
  color: var(--pl-text-primary);
  background: none;
  border: none;
  outline: none;
  z-index: var(--pl-z-index-level2);
  transition: 0.3s;
  margin: 0 var(--pl-unit-x1);
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::placeholder {
    color: var(--pl-text-secondary);
  }

  .pl-input_has-focus &::placeholder {
    color: var(--pl-text-tertiary);
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--pl-text-tertiary);
  }

  .pl-input_has-value.pl-input_has-label &,
  .pl-input_has-label.pl-input_has-focus & {
    padding-top: var(--pl-unit-x4);
    padding-bottom: var(--pl-unit-x1);
  }
}

.pl-input-label {
  position: absolute;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  top: 10px;
  left: 0;
  right: 0;
  padding: 2px 44px 0 16px;
  font: var(--pl-font-body-large-regular);
  color: var(--pl-text-secondary);
  transition: 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  transition-duration: 0.3s;
  z-index: var(--pl-z-index-level1);

  .pl-input_has-left-icon & {
    padding-left: 44px;
  }

  .pl-input_has-disabled & {
    color: var(--pl-text-tertiary);
  }

  .pl-input_has-value &,
  .pl-input_has-focus & {
    top: 0;
    font: var(--pl-font-caption-large-regular-low);
  }

  .pl-input_has-required & {
    &::after {
      content: "*";
    }
  }

  .pl-input_error.pl-input_has-value &,
  .pl-input_error.pl-input_has-focus & {
    color: var(--pl-text-secondary);
  }
}

.pl-input-prefix {
  order: -1;
  font: var(--pl-font-body-large-regular);
  color: var(--pl-text-secondary);
  z-index: var(--pl-z-index-level1);
  margin-left: var(--pl-unit-x1);

  .pl-input_has-label & {
    display: none;
  }
}

.pl-input-suffix {
  font: var(--pl-font-body-large-regular);
  color: var(--pl-text-secondary);
  z-index: var(--pl-z-index-level1);

  .pl-input_has-label & {
    display: none;
  }

  .pl-input_has-right-icon &,
  .pl-input_has-clear.pl-input_has-value & {
    margin-right: var(--pl-unit-x1);
  }
}

.pl-input-icon-left {
  order: -2;
  z-index: var(--pl-z-index-level1);
  display: flex;
  align-items: center;
  margin-right: var(--pl-unit-x1);
  color: var(--pl-icon-secondary);
}

.pl-input-clear {
  cursor: pointer;
  z-index: var(--pl-z-index-level2);
  display: flex;
}

.pl-input-icon-right {
  z-index: var(--pl-z-index-level1);
  display: flex;
  align-items: center;
  width: var(--pl-unit-x6);
  flex-shrink: 0;
  color: var(--pl-icon-secondary);

  .pl-input-clear + & {
    margin-left: var(--pl-unit-x1);
  }
}

.pl-input-hint,
.pl-input-hint-error {
  padding: 0 var(--pl-unit-x4);
  font: var(--pl-font-caption-large-regular);
}

.pl-input-hint {
  display: block;
  margin-top: var(--pl-unit-x1);
  color: var(--pl-text-secondary);

  .pl-input_error & {
    display: none;
  }
}

.pl-input-hint-error {
  display: none;
  margin-top: var(--pl-unit-x1);
  color: var(--pl-text-system-error);

  .pl-input_error & {
    display: block;
  }
}
</style>
